import {
	createCustomSlider,
	EternalSlidingMode,
	ICustomPhotoSliderItem,
	ICustomPhotoSliderWithDivSettings,
	ICustomSlider,
} from "../components/customslider";
import { expressQuerySelector, expressQuerySelectorAll } from "../common/html";
import { initVideoPlayer } from "../components/video-player";
import { getParameter, updateParameter } from "../utils/querystring";

export enum CarrouselItemType {
	FormatImage = 0,
	PackShot = 1
}

export interface ICarrousel {
	readonly reInit: () => void;
	readonly reStart: () => void;
	readonly resize: () => void;
	readonly addAndSelectElement: (imgSrc: string) => boolean;
	readonly removeFirstElementAndSelect: (itemType: CarrouselItemType) => void;
	readonly getCurrentElement: (sliderIndex: number) => ICustomPhotoSliderItem;
	readonly getAllElements: (sliderIndex: number) => ICustomPhotoSliderItem[];
	readonly removeSliderElement: (sliderIndex: number, visiblePhotoEl: ICustomPhotoSliderItem) => void;
	readonly goToSlide: (currentSlide: ICustomPhotoSliderItem) => void;
	readonly dispose: () => void;
}
export interface ICarrouselOptions {
	readonly initFormatImage?: (sliderElement: any) => void;
}

enum SideButtonOption {
	Prev,
	Next
}

export function createCarrousel(containerEl: HTMLElement, opts?: ICarrouselOptions) {
	const sliderComponents: ICustomSlider[] = [];
	let currentlySelectedEl: Element;
	const VIDEO_EXTENSIONS = ['mp4', 'webm'];
	let youtubePlayer: {
		dispose: () => void,
		playPauseYoutubeVideo: (action: "play" | "pause") => void,
		showOverlay: () => void,
		isOverlayVisible: () => boolean | null
	} | undefined;

	const GetHtmlElementWidthWithMargins = (element: HTMLElement): number => {
		if (!element) return 0;
		const style = window.getComputedStyle(element),
			width = style.width ? parseFloat(style!.width) : element.clientWidth, // use this instead of client width to handle the rounding of the elements => worse case 1 px of the image is not shown
			margin = parseFloat(style.marginLeft ? style.marginLeft : '0') + parseFloat(style.marginRight ? style.marginRight : '0'),
			padding = parseFloat(style.paddingLeft ? style.paddingLeft : '0') + parseFloat(style.paddingRight ? style.paddingRight : '0');
		return (width + (margin ? margin : 0) + (padding ? padding : 0));
	};

	const resizeSliderElements = (settings: ICustomPhotoSliderWithDivSettings, sliderWidth: number, slider: HTMLElement, sliderChildren?: HTMLElement[]) => {
		if (sliderWidth <= 0 || (settings.sliderContainerSettings.extraSliderClass && settings.sliderContainerSettings.extraSliderClass.indexOf('technical-preview-container') === -1)) return;

		// set all slider elements to the width of there slide element so only 1 slide is visible at a time
		const slidesInSlider = sliderChildren ? sliderChildren : expressQuerySelectorAll<HTMLElement>(slider, '.technical-slider-element');
		for (let i = 0; i < slidesInSlider.length; i++) {
			const slide = slidesInSlider[i];
			slide.style.minWidth = sliderWidth + 'px';
			slide.style.width = sliderWidth + 'px';
			slide.style.maxWidth = sliderWidth + 'px';
		}

		// block iframe too on width
		const iframesInSlider = expressQuerySelectorAll<HTMLIFrameElement>(slider, 'iframe');
		const sliderWidthForIframe = sliderWidth - 1; // iframe always .2 or more => 1 more px on page
		for (let z = 0; z < iframesInSlider.length; z++) {
			const iframe = iframesInSlider[z];
			iframe.width = sliderWidthForIframe.toString();
			iframe.style.minWidth = sliderWidthForIframe + 'px';
			iframe.style.width = sliderWidthForIframe + 'px';
			iframe.style.maxWidth = sliderWidthForIframe + 'px';
		}
	};

	const playPauseVideo = () => {
		if (!youtubePlayer || youtubePlayer.isOverlayVisible()) return;
		youtubePlayer.playPauseYoutubeVideo('pause');
	};

	const isElementVisibleInContainer = (element, container) => {
		const rect = element.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();
		return rect.left >= containerRect.left && rect.right <= containerRect.right;
	};

	const onSideButtonClicked = (e: Event, eyeCatcher: Element[], option: SideButtonOption) => {
		playPauseVideo();
		eyeCatcher.forEach(x => x.classList.toggle('u-hide', (youtubePlayer && !youtubePlayer.isOverlayVisible()) || false));
		const target = e.currentTarget as HTMLElement;
		if (target.classList.contains('technical-thumb-container') || target.parentElement.classList.contains('technical-thumb-container')) return;

		updateImageCount(sliderComponents[0], eyeCatcher);

		if (currentlySelectedEl) {
			const element = option === SideButtonOption.Prev
				? currentlySelectedEl.previousElementSibling || currentlySelectedEl.parentElement.lastElementChild
				: currentlySelectedEl.nextElementSibling || currentlySelectedEl.parentElement.firstElementChild;
			if (element) {
				changeSelectedElement(element);
				const bottomSlider = sliderComponents[1];
				if (bottomSlider) {
					const isFullyVisible = isElementVisibleInContainer(element, bottomSlider.GetSliderElement());
					if (isFullyVisible) return;
					const sliderElement = bottomSlider.Settings.sliderElements.find(x => x.id === element.id);
					bottomSlider.JumpIntoView(sliderElement, undefined, option === SideButtonOption.Prev ? 'start' : 'end');
				}
			}
		}
	};

	const updateImageCount = (sliderComponent: ICustomSlider, eyeCatcher?: Element[]) => {
		if (!sliderComponent) return;

		const imageCountEl = (eyeCatcher || expressQuerySelectorAll(sliderComponent.GetSliderElement(), '.technical-carrousel-eyecatcher')).find(x => x.classList.contains('technical-image-eyecatcher'));
		if (!imageCountEl) return;
		imageCountEl.innerHTML = `${sliderComponent.Settings.sliderElements.indexOf(sliderComponent.GetCurrentSlide()) + 1}/${sliderComponent.Settings.sliderElements.length}`;
	};

	const changeSelectedElement = (newElement: Element) => {
		currentlySelectedEl?.classList.toggle('m-carrousel__thumb--active', false);
		newElement.classList.toggle('m-carrousel__thumb--active', true);
		currentlySelectedEl = newElement;
	};

	const init = () => {
		if (!sliderComponents || sliderComponents.length === 0) {
			// both sliders in m-carrousel
			const sliders = expressQuerySelectorAll<HTMLElement>(containerEl, '.technical-slider');

			for (let i = 0; i < sliders.length; i++) {
				const sliderEl = sliders[i];

				// convert so slider elements
				const sliderElements: ICustomPhotoSliderItem[] = [];

				for (let z = 0, l = sliderEl.children.length; z < l; z++)
					sliderElements.push({ template: sliderEl.children[z] as HTMLElement });

				// settings for slider
				const eyeCatcher = expressQuerySelectorAll(sliderEl.parentElement, '.technical-carrousel-eyecatcher');
				const settings = {
					sliderElements: sliderElements,
					sliderContainerSettings: {
						extraSliderClass: 'm-carrousel__slider ' + (i === 0 ? 'technical-preview-container ' : 'technical-thumb-container '),
						elementsContainerSettings: {
							extraSliderClass: 'm-carrousel__slider-elements',
							slideClicked: (e: Event, settings: ICustomPhotoSliderWithDivSettings) => {
								// play youtube video from first slider
								const target = (e.target as HTMLElement).closest('.technical-slider-element') as HTMLElement;
								if (target && target.classList.contains('technical-video-container') && youtubePlayer && (settings.sliderContainerSettings.extraSliderClass && settings.sliderContainerSettings.extraSliderClass.indexOf('technical-preview-container') !== -1))
									youtubePlayer.playPauseYoutubeVideo(youtubePlayer.isOverlayVisible() ? 'play' : 'pause');
								else if (youtubePlayer && !youtubePlayer.isOverlayVisible() && (settings.sliderContainerSettings.extraSliderClass && settings.sliderContainerSettings.extraSliderClass.indexOf('technical-thumb-container') !== -1))
									youtubePlayer.playPauseYoutubeVideo('pause');

								eyeCatcher.forEach(x => x.classList.toggle('u-hide', (youtubePlayer && !youtubePlayer.isOverlayVisible()) || false));

								// update sliders
								if (!e.target || (settings.sliderContainerSettings.extraSliderClass && settings.sliderContainerSettings.extraSliderClass.indexOf('technical-preview-container') >= 0)) return;
								let clickedItemTemplate = ((e.target as HTMLElement).closest('.q-video-cover-overlay__button') || (e.target as HTMLElement).closest('img') || (e.target as HTMLElement).closest('picture') || (e.target as HTMLElement).closest('video')) as HTMLImageElement;
								if ((!clickedItemTemplate && (e.target as HTMLElement).classList.contains("m-carrousel__cover-container"))
									|| (clickedItemTemplate && clickedItemTemplate.classList.contains("q-video-cover-overlay__button"))) {
									const el = (e.target as HTMLElement).closest('.technical-slider-element');
									const imgs = (el.getElementsByTagName('img') || el.getElementsByTagName('picture') || el.getElementsByTagName('video')) as HTMLCollectionOf<HTMLImageElement>;
									if (imgs.length) clickedItemTemplate = imgs[0];
								}
								if (!clickedItemTemplate) return;

								const clickedItemTemplateSrc = clickedItemTemplate instanceof HTMLVideoElement ? expressQuerySelector(clickedItemTemplate, 'source')?.src : clickedItemTemplate.src;

								// get all sliders of the page except own slider
								for (let i = 0; i < sliderComponents.length; i++) {
									const sliderComponent = sliderComponents[i];
									const sliderEl = sliderComponent.GetSliderElement();
									const sliderElement = sliderComponent.Settings.sliderElements.filter(x => {
										if (!x.template) return;
										const sliderItemTemplate = expressQuerySelector<HTMLImageElement>(x.template, 'img')
											|| expressQuerySelector<HTMLIFrameElement>(x.template, 'iframe')
											|| expressQuerySelector<HTMLIFrameElement>(x.template, 'picture')
											|| expressQuerySelector<HTMLSourceElement>(x.template, 'video > source');
										return sliderItemTemplate instanceof HTMLIFrameElement
											? sliderItemTemplate && sliderItemTemplate.parentElement && sliderItemTemplate.parentElement.classList.contains('technical-video-container') && clickedItemTemplate.classList.contains('m-carrousel__thumb-video-img')
											: sliderItemTemplate && (sliderItemTemplate.dataset.src || sliderItemTemplate.src) === clickedItemTemplateSrc;
									});

									// show eye catcher if the video is not playing
									const sliderEyeCatchers = expressQuerySelectorAll(sliderEl, '.technical-carrousel-eyecatcher');
									sliderEyeCatchers.forEach(x => x.classList.toggle('u-hide', (youtubePlayer && !youtubePlayer.isOverlayVisible()) || false));

									// slider has not the same element
									if (sliderElement.length <= 0)
										continue;

									const element = sliderElement[0];

									if (element.template.classList.contains('m-carrousel__thumb')) {
										const clickedEl = expressQuerySelector(sliderEl, `#${element.id}`);
										changeSelectedElement(clickedEl);
									}

									// don't jump into view if thumbs are all shown already
									const sliderButtonRight = expressQuerySelector(sliderEl, '.m-carrousel__button--right', true);
									const sliderButtonLeft = expressQuerySelector(sliderEl, '.m-carrousel__button--left', true);
									if (settings.sliderContainerSettings.extraSliderClass && settings.sliderContainerSettings.extraSliderClass.indexOf('technical-thumb-container') !== -1
										&& sliderButtonRight.classList.contains(settings.sliderContainerSettings.nextButton.cssWhenOnMax)
										&& sliderButtonLeft.classList.contains(settings.sliderContainerSettings.previousButton.cssWhenOnMax))
										continue;

									// slider has the same element jump into view
									sliderComponent.JumpIntoView(element);

									// update image count
									updateImageCount(sliderComponent, sliderEyeCatchers);
								}
							}
						},
						nextButton: {
							css: `a-button a-button--slider-button m-carrousel__button--right ${i === 0 ? 'm-carrousel__button--large' : ''} icon-arrow-right technical-slider-button ${sliderElements.length <= 1 ? 'u-hide' : ''}`,
							onClick: (e: Event) => {
								onSideButtonClicked(e, eyeCatcher, SideButtonOption.Next);
							}
						},
						previousButton: {
							css: `a-button a-button--slider-button m-carrousel__button--left ${i === 0 ? 'm-carrousel__button--large' : ''} icon-arrow-left technical-slider-button ${sliderElements.length <= 1 ? 'u-hide' : ''}`,
							onClick: (e: Event) => {
								onSideButtonClicked(e, eyeCatcher, SideButtonOption.Prev);
							}
						},
					},
					translateForMovement: i == 0,
					SliderCreated: (settings: ICustomPhotoSliderWithDivSettings, slider: HTMLElement, oldSlider: HTMLElement | undefined) => {
						const sliderWidth = Math.ceil(GetHtmlElementWidthWithMargins(oldSlider ? oldSlider : sliderEl));
						resizeSliderElements(settings, sliderWidth, slider, expressQuerySelectorAll<HTMLElement>(slider, '.technical-slider-element'));

						// only for thumb slider
						if ((settings.sliderContainerSettings.extraSliderClass && settings.sliderContainerSettings.extraSliderClass.indexOf('technical-thumb-container') !== -1)) {
							// add extra div for styling of the thumbs
							const sliderElementsEl = expressQuerySelector<HTMLElement>(slider, '.technical-slider-elements', true);
							const extraDivForStyling = document.createElement('div');
							extraDivForStyling.classList.add('m-carrousel__slider-elements-container');
							extraDivForStyling.appendChild(sliderElementsEl);
							slider.appendChild(extraDivForStyling);
							return;
						}

						// init video players
						const videoPlayers = expressQuerySelectorAll<HTMLElement>(slider, '.technical-video-container');
						for (let i = 0; i < videoPlayers.length; i++) {
							const videoPlayerContainer = videoPlayers[i];

							// create video player
							youtubePlayer = initVideoPlayer(videoPlayerContainer, false);
						}
					},
					ResizeEvent: (settings, slider) => {
						slider = sliderComponent.GetSliderElement();
						// get current slide to reset to that slide after resize
						// get the current selected from thumb => after resize the slides get other widths => calculation for the current selected slide could be wrong then
						const currentSlide = i === 0 && sliderComponents.length > 1 && currentlySelectedEl
							? sliderComponents[0].Settings.sliderElements[sliderComponents[1].Settings.sliderElements.findIndex(x => x.id === currentlySelectedEl.id)]
							: sliderComponent.GetCurrentSlide();
						if (!currentSlide) return;

						const sliderWidth = Math.ceil(GetHtmlElementWidthWithMargins(slider));
						resizeSliderElements(settings, sliderWidth, slider);
						sliderComponent.JumpIntoView(currentSlide, false);
						updateImageCount(sliderComponent);
					},
					InitializedEvent: (settings, slider: HTMLElement) => {
						eyeCatcher.forEach(x => {
							slider.prepend(x);
							x.classList.remove('u-hide');
						});

						slider.classList.add("m-carrousel__slider--initialized");
						if (settings && opts && opts.initFormatImage)
							opts.initFormatImage(settings);
					}
				} as ICustomPhotoSliderWithDivSettings;

				const sliderComponent = createCustomSlider(sliderEl, settings);
				sliderComponents.push(sliderComponent);
			}
		} else {
			sliderComponents.forEach(slider => slider.AddHandlers());
		}

		const bottomSlider = sliderComponents[1];
		if (bottomSlider) {
			const firstEl = bottomSlider.GetSliderElement() && expressQuerySelector(bottomSlider.GetSliderElement(), `#${bottomSlider.Settings.sliderElements[0].id}`);
			firstEl && changeSelectedElement(firstEl);
		}
	};

	const reStart = () => {
		sliderComponents.forEach(slider => {
			const slideEls = slider.Settings.sliderElements;
			if (slideEls.length > 1) {
				if (slideEls[0]) {
					slider.JumpIntoView(slideEls[0], false);
					updateImageCount(slider);
				}
				slider.ShowButtons();
			} else {
				slider.HideButtons();
			}
		});
	};

	const moveBottomSliderToSelectedItem = () => {
		const topSlider = sliderComponents[0];
		const bottomSlider = sliderComponents[1];

		if (!bottomSlider) return;

		const topSliderSelectedItem = topSlider
			? topSlider.Settings.sliderElements.indexOf(topSlider.GetCurrentSlide())
			: -1;

		// top and bottom should have the same elements on the same index
		const bottomSliderToSelectEl = bottomSlider.GetSliderElement && expressQuerySelector(bottomSlider.GetSliderElement(), `#${bottomSlider.Settings.sliderElements[topSliderSelectedItem > -1 ? topSliderSelectedItem : 0].id}`);
		bottomSliderToSelectEl && changeSelectedElement(bottomSliderToSelectEl);

	};

	const removeFirstElementAndSelect = (itemType: CarrouselItemType) => {
		sliderComponents.forEach(slider => {
			const isPreviewSlider = slider.Settings.sliderContainerSettings.extraSliderClass.includes('technical-preview-container');

			switch (itemType) {
				case CarrouselItemType.FormatImage:
					if (slider.Settings && slider.Settings.sliderElements && slider.Settings.sliderElements.length > 0) {
						const currentSlide = slider.GetCurrentSlide();
						const currentSelected = currentSlide.id == slider.Settings.sliderElements[0].id
							? slider.Settings.sliderElements[1]
							: currentSlide;

						slider.RemoveSliderElementOnIndex(0);
						!isPreviewSlider && slider.JumpIntoView(currentSelected, false);
					}
					else
						return;
					updateImageCount(slider);
					moveBottomSliderToSelectedItem();
					break;
				case CarrouselItemType.PackShot:
					if (slider.Settings && slider.Settings.sliderElements && slider.Settings.sliderElements.length > 0 && slider.Settings.sliderElements[0].template.classList.contains("technical-packshot")) {
						const currentSlide = slider.GetCurrentSlide();
						const currentSelected = currentSlide.id == slider.Settings.sliderElements[0].id
							? slider.Settings.sliderElements[1]
							: currentSlide;

						slider.RemoveSliderElementOnIndex(0);
						!isPreviewSlider && slider.JumpIntoView(currentSelected, false);
						updateImageCount(slider);
						moveBottomSliderToSelectedItem();

						let newUrl = document.location.href;
						const design = getParameter('design', newUrl, false);
						if (design) {
							newUrl = updateParameter("design", "");
							if (document.location.href !== newUrl)
								history.replaceState({}, '', newUrl);
						}
					}
					else
						return;
					break;
			}
		});

	};

	const addAndSelectElement = (imgSrc: string): boolean => {
		if (!imgSrc) return false;
		[...sliderComponents].reverse().forEach(slider => {
			// do a deep clone of a non video element so there is no reference to the current slide template anymore => else the thumb click does nothing anymore
			const formatImageContainerTemplate = slider.GetAllSlides().find(x => x.template.querySelector('img') && !x.template.querySelector('.technical-overlay')).template.cloneNode(true) as HTMLElement;
			const extension = imgSrc.substring(imgSrc.lastIndexOf('.') + 1, imgSrc.length);
			const isPreviewSlider = slider.Settings.sliderContainerSettings.extraSliderClass.includes('technical-preview-container');

			if (VIDEO_EXTENSIONS.some(x => x === extension)) {
				formatImageContainerTemplate.classList.remove('m-carrousel__preview-image-container');

				formatImageContainerTemplate.classList.toggle('m-carrousel__preview-video-container', isPreviewSlider);

				const videoContainer = document.createElement('video');
				videoContainer.classList.add(isPreviewSlider ? 'm-carrousel__preview-local-video' : 'm-carrousel__thumb-local-video');
				videoContainer.loop = true;
				videoContainer.muted = true;
				videoContainer.preload = 'metadata';
				videoContainer['disableRemotePlayback'] = true;
				videoContainer.crossOrigin = 'anonymous';
				videoContainer.playsInline = true;
				videoContainer.autoplay = true;

				const videoContainerSource = document.createElement('source');
				videoContainerSource.src = imgSrc;
				videoContainerSource.type = `video/${extension}`;

				formatImageContainerTemplate.innerHTML = ''; // remove all

				videoContainer.appendChild(videoContainerSource);
				formatImageContainerTemplate.appendChild(videoContainer);
			} else {
				const imgEl = expressQuerySelector<HTMLImageElement>(formatImageContainerTemplate, "img", true);
				imgEl.src = imgSrc;
				const sourceEl = expressQuerySelector<HTMLSourceElement>(formatImageContainerTemplate, "source", false);
				if (sourceEl) {
					sourceEl.srcset = imgSrc.endsWith('.jpg') ? imgSrc + '.webp' : imgSrc;
				}
			}

			// add correct dimentions
			const sliderEl = slider.GetSliderElement();
			const element = isPreviewSlider
				? sliderEl
				: expressQuerySelector<HTMLElement>(sliderEl, `#${slider.Settings.sliderElements[0].id}`);

			const elementStyle = window.getComputedStyle(element),
				elementWidth = elementStyle.width ? parseFloat(elementStyle!.width) : element.clientWidth; // use this instead of client width to handle the rounding of the elements => worse case 1 px of the image is not shown

			formatImageContainerTemplate.style.width = `${elementWidth}px`;
			formatImageContainerTemplate.style.minWidth = `${elementWidth}px`;
			formatImageContainerTemplate.style.maxWidth = `${elementWidth}px`;

			slider.AddSliderElement({ template: formatImageContainerTemplate }, 0);
			updateImageCount(slider);

			// update bottom slider
			if (!isPreviewSlider) {
				const firstEl = slider.GetSliderElement && expressQuerySelector(slider.GetSliderElement(), `#${slider.Settings.sliderElements[0].id}`);
				firstEl && changeSelectedElement(firstEl);
			}

			// trigger resize for container to be the same width as other elements
			const sliderSettings = slider.Settings;
			if (sliderSettings.ResizeEvent) sliderSettings.ResizeEvent(sliderSettings, sliderEl);
		});

		return true;
	};

	const getCurrentElement = (sliderIndex: number): ICustomPhotoSliderItem => {
		return sliderComponents[sliderIndex].GetCurrentSlide();
	};

	const getAllElements = (sliderIndex: number): ICustomPhotoSliderItem[] => {
		return sliderComponents[sliderIndex].GetAllSlides();
	};

	const resize = () => {
		sliderComponents.forEach(slider => {
			const sliderEl = slider.GetSliderElement();
			const sliderWidth = Math.ceil(GetHtmlElementWidthWithMargins(sliderEl));
			resizeSliderElements(slider.Settings, sliderWidth, sliderEl);
		});
	};

	const removeSliderElement = (sliderIndex: number, visiblePhotoEl: ICustomPhotoSliderItem) => {
		sliderComponents[sliderIndex].RemoveSliderElement(visiblePhotoEl);
	};

	const dispose = () => {
		sliderComponents.forEach(slider => {
			slider.Dispose();
		});
	};

	init();

	const reInit = () => {
		init();
	};

	return {
		reInit,
		reStart,
		resize,
		addAndSelectElement,
		removeFirstElementAndSelect,
		getCurrentElement,
		getAllElements,
		removeSliderElement,
		goToSlide: (currentSlide: ICustomPhotoSliderItem) => sliderComponents.forEach(slider => {
			slider.JumpIntoView(currentSlide, false);
			updateImageCount(slider);
		}),
		dispose
	} as ICarrousel;
}
